import { useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react'
import { Spin } from 'antd'
import Guacamole, {
  type Client,
  type InputStream,
  type Keyboard,
  type Mouse,
  type MouseEventPayload,
  type Tunnel,
} from 'guacamole-common-js'
import {
  KEYSYM_ALT,
  KEYSYM_CTRL,
  KEYSYM_DELETE,
  resolveTunnelUrl,
} from './types'
import {
  bindGuacamoleSession,
  disposeGuacamoleSession,
  setGuacamoleKeyboard,
} from './guacamoleLifecycle'
import styles from './GuacamoleSession.module.css'

export interface GuacamoleSessionHandle {
  disconnect: () => void
  sendCtrlAltDel: () => void
  sendClipboard: (text: string) => void
}

interface Props {
  tunnelUrl: string
  connectionToken: string
  sessionKey?: string | number
  onStateChange?: (state: number, label: string) => void
  onError?: (message: string) => void
  onConnected?: () => void
  onContentSize?: (width: number, height: number) => void
}

function stateLabel(state: number): string {
  switch (state) {
    case Guacamole.Client.State.IDLE:
      return 'Ожидание'
    case Guacamole.Client.State.CONNECTING:
      return 'Подключение…'
    case Guacamole.Client.State.WAITING:
      return 'Ожидание сервера…'
    case Guacamole.Client.State.CONNECTED:
      return 'Подключено'
    case Guacamole.Client.State.DISCONNECTING:
      return 'Отключение…'
    case Guacamole.Client.State.DISCONNECTED:
      return 'Отключено'
    default:
      return 'Статус неизвестен'
  }
}

function readClipboardStream(
  stream: InputStream,
  mimetype: string,
  onText: (text: string) => void,
) {
  if (!mimetype.startsWith('text/')) {
    stream.sendAck('Only text clipboard supported', 0x0100)
    return
  }
  const reader = new Guacamole.StringReader(stream)
  let data = ''
  reader.ontext = (text) => {
    data += text
  }
  reader.onend = () => {
    onText(data)
  }
}

function writeClipboard(client: Client, text: string) {
  if (!text) return
  const stream = client.createClipboardStream('text/plain')
  const writer = new Guacamole.StringWriter(stream)
  writer.sendText(text)
  writer.sendEnd()
}

export const GuacamoleSession = forwardRef<GuacamoleSessionHandle, Props>(
  function GuacamoleSession(
    {
      tunnelUrl,
      connectionToken,
      sessionKey,
      onStateChange,
      onError,
      onConnected,
      onContentSize,
    },
    ref,
  ) {
    const viewportRef = useRef<HTMLDivElement>(null)
    const displayHostRef = useRef<HTMLDivElement>(null)
    const clientRef = useRef<Client | null>(null)
    const resizeObserverRef = useRef<ResizeObserver | null>(null)
    const disposedRef = useRef(false)
    const [status, setStatus] = useState('Подключение…')
    const [error, setError] = useState<string | null>(null)
    const [connected, setConnected] = useState(false)

    const onStateChangeRef = useRef(onStateChange)
    const onErrorRef = useRef(onError)
    const onConnectedRef = useRef(onConnected)
    const onContentSizeRef = useRef(onContentSize)
    onStateChangeRef.current = onStateChange
    onErrorRef.current = onError
    onConnectedRef.current = onConnected
    onContentSizeRef.current = onContentSize

    useImperativeHandle(ref, () => ({
      disconnect: () => {
        disposedRef.current = true
        resizeObserverRef.current?.disconnect()
        resizeObserverRef.current = null
        disposeGuacamoleSession()
        clientRef.current = null
        if (displayHostRef.current) displayHostRef.current.innerHTML = ''
        setConnected(false)
      },
      sendCtrlAltDel: () => {
        const client = clientRef.current
        if (!client || disposedRef.current) return
        ;[KEYSYM_CTRL, KEYSYM_ALT, KEYSYM_DELETE].forEach((k) => client.sendKeyEvent(1, k))
        ;[KEYSYM_DELETE, KEYSYM_ALT, KEYSYM_CTRL].forEach((k) => client.sendKeyEvent(0, k))
      },
      sendClipboard: (text: string) => {
        if (clientRef.current && !disposedRef.current) writeClipboard(clientRef.current, text)
      },
    }))

    useEffect(() => {
      const viewport = viewportRef.current
      const displayHost = displayHostRef.current
      if (!viewport || !displayHost || !connectionToken) return

      disposedRef.current = false
      setError(null)
      setConnected(false)
      setStatus('Подключение…')
      displayHost.innerHTML = ''

      const resolvedTunnel = resolveTunnelUrl(tunnelUrl)
      const tunnel: Tunnel = new Guacamole.HTTPTunnel(resolvedTunnel, true)
      const client = new Guacamole.Client(tunnel)
      clientRef.current = client
      bindGuacamoleSession({ client, tunnel, keyboard: null })

      const display = client.getDisplay()

      tunnel.onerror = (st) => {
        if (disposedRef.current) return
        const msg = st?.message || 'Ошибка туннеля Guacamole'
        setError(msg)
        setStatus(msg)
        onErrorRef.current?.(msg)
      }

      client.onerror = (st) => {
        if (disposedRef.current) return
        const msg = st?.message || 'Ошибка подключения Guacamole'
        setError(msg)
        setStatus(msg)
        onErrorRef.current?.(msg)
      }

      client.onclipboard = (stream, mimetype) => {
        readClipboardStream(stream, mimetype, (text) => {
          if (!text || disposedRef.current) return
          void navigator.clipboard?.writeText(text).catch(() => {
            /* permission denied */
          })
        })
      }

      const scaleDisplay = () => {
        if (!displayHost || disposedRef.current) return
        const dw = display.getWidth()
        const dh = display.getHeight()
        const cw = displayHost.clientWidth
        const ch = displayHost.clientHeight
        if (dw > 0 && dh > 0) {
          onContentSizeRef.current?.(dw, dh)
        }
        if (dw > 0 && dh > 0 && cw > 0 && ch > 0) {
          const scale = Math.min(cw / dw, ch / dh)
          display.scale(scale)
          const el = display.getElement()
          const sw = dw * scale
          const sh = dh * scale
          el.style.left = `${Math.max(0, (cw - sw) / 2)}px`
          el.style.top = `${Math.max(0, (ch - sh) / 2)}px`
        }
      }

      let mouse: Mouse | null = null
      let keyboard: Keyboard | null = null

      const attachInput = () => {
        if (disposedRef.current) return
        const displayEl = display.getElement()
        displayEl.style.position = 'absolute'
        displayEl.style.top = '0'
        displayEl.style.left = '0'
        displayEl.style.transformOrigin = 'top left'
        displayHost.innerHTML = ''
        displayHost.appendChild(displayEl)

        mouse = new Guacamole.Mouse(displayEl)
        const sendMouse = (e: MouseEventPayload) => {
          if (disposedRef.current) return
          client.sendMouseState(e.state, true)
        }
        mouse.onEach(['mousedown', 'mousemove', 'mouseup', 'mouseout'], sendMouse)

        display.oncursor = (canvas, x, y) => {
          if (canvas && mouse && mouse.setCursor(canvas, x, y)) {
            display.showCursor(false)
          } else {
            display.showCursor(!!canvas)
          }
        }

        keyboard = new Guacamole.Keyboard(document)
        setGuacamoleKeyboard(keyboard)
        keyboard.onkeydown = (keysym: number) => {
          if (disposedRef.current) return true
          if (keysym === 0xff1b) return true
          client.sendKeyEvent(1, keysym)
        }
        keyboard.onkeyup = (keysym: number) => {
          if (disposedRef.current) return true
          if (keysym === 0xff1b) return true
          client.sendKeyEvent(0, keysym)
        }

        display.onresize = () => scaleDisplay()
        scaleDisplay()
        window.setTimeout(scaleDisplay, 100)
        window.setTimeout(scaleDisplay, 400)

        if (typeof ResizeObserver !== 'undefined') {
          resizeObserverRef.current?.disconnect()
          const ro = new ResizeObserver(() => scaleDisplay())
          ro.observe(displayHost)
          resizeObserverRef.current = ro
        }
      }

      client.onstatechange = (state) => {
        if (disposedRef.current) return
        const label = stateLabel(state)
        setStatus(label)
        onStateChangeRef.current?.(state, label)

        if (state === Guacamole.Client.State.CONNECTED) {
          setConnected(true)
          setError(null)
          attachInput()
          onConnectedRef.current?.()
          viewport.focus()
        }

        if (state === Guacamole.Client.State.DISCONNECTED) {
          setConnected(false)
          setGuacamoleKeyboard(null)
          keyboard = null
        }
      }

      const onPaste = (e: ClipboardEvent) => {
        if (disposedRef.current) return
        const text = e.clipboardData?.getData('text/plain')
        if (text) writeClipboard(client, text)
      }

      const syncLocalClipboard = () => {
        if (disposedRef.current) return
        void navigator.clipboard
          ?.readText()
          .then((text) => {
            if (text && !disposedRef.current) writeClipboard(client, text)
          })
          .catch(() => {
            /* no permission */
          })
      }

      const onFocusWindow = () => {
        if (document.hasFocus()) syncLocalClipboard()
      }

      viewport.addEventListener('paste', onPaste)
      window.addEventListener('focus', onFocusWindow)

      try {
        client.connect(connectionToken)
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)
        setError(msg)
        onErrorRef.current?.(msg)
      }

      return () => {
        disposedRef.current = true
        viewport.removeEventListener('paste', onPaste)
        window.removeEventListener('focus', onFocusWindow)
        resizeObserverRef.current?.disconnect()
        resizeObserverRef.current = null
        disposeGuacamoleSession()
        clientRef.current = null
        displayHost.innerHTML = ''
      }
    }, [tunnelUrl, connectionToken, sessionKey])

    return (
      <div
        ref={viewportRef}
        className={styles.viewport}
        tabIndex={0}
        role="application"
        aria-label="Удалённый рабочий стол"
      >
        <div ref={displayHostRef} className={styles.display} />
        {!connected || error ? (
          <div className={`${styles.overlay} ${error ? styles.overlayError : ''}`}>
            <div className={styles.overlayInner}>
              {!error ? <Spin /> : null}
              <span>{error || status}</span>
            </div>
          </div>
        ) : null}
      </div>
    )
  },
)
